/**
 * "Add to Project" button shown next to generated downloads and ad hoc uploads.
 */
import { memo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Snackbar, Alert } from "@mui/material";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import { captureEvent, AA_EVENTS } from "src/config/posthog";
import { useChatContext } from "src/reusable/Chat/context/ChatContext";
import {
  useFolder,
  useAddFromWorkspace,
} from "src/api/projectFolders/projectFolderService";
import { useGetDialoguesById } from "src/views/assistant/AssistantServices";
import ProjectPickerDialog from "src/features/library/dialogs/ProjectPickerDialog";
import ReplaceFileDialog, {
  PromoteConflict,
} from "src/views/assistant/projects/components/ReplaceFileDialog";
import type { ProjectFolder } from "src/api/projectFolders/types";

interface Props {
  artifactId?: number;
  sdrId?: number;
  filename: string;
  /**
   * Fired after a successful add with the destination + (possibly
   * deconflicted) stored name. When provided, the caller owns the post-add
   * feedback (e.g. the generated-doc card's "Added to {project}" + focus-jump,
   * #20423 WS7) and the built-in "View project" snackbar is suppressed.
   */
  onAdded?: (added: {
    folderId: number;
    folderName: string | null;
    fileName: string;
  }) => void;
}

const AddToProjectButton = memo(
  ({ artifactId, sdrId, filename, onAdded }: Props) => {
    const { dialogueId } = useChatContext();
    const history = useHistory();
    const [open, setOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [conflict, setConflict] = useState<PromoteConflict | null>(null);
    const [success, setSuccess] = useState<{
      text: string;
      folderId: number;
    } | null>(null);
    const { data: dialogue } = useGetDialoguesById(dialogueId);
    const addFromWorkspace = useAddFromWorkspace();

    const source = sdrId ? "dialogue_adhoc" : "agent_workspace";

    const originFolderId: number | null = dialogue?.project_folder_id ?? null;
    const { data: originFolder } = useFolder(originFolderId);

    if (!dialogueId || (source === "agent_workspace" && !artifactId))
      return null;

    // Ad-hoc upload promotion is a project-chat-only surface: it adds the upload
    // to the chat's own project folder. In a general chat (no project folder)
    // it is intentionally absent. Agent-workspace promotion stays available in
    // every chat and is unaffected by this gate.
    if (source === "dialogue_adhoc" && originFolderId === null) return null;

    const addToFolder = async (folderId: number, folderName: string | null) => {
      const requestData = {
        source,
        dialogue_id: dialogueId,
        ...(artifactId ? { artifact_id: artifactId } : {}),
        ...(sdrId ? { sdr_id: sdrId } : {}),
      } as const;
      try {
        const result = await addFromWorkspace.mutateAsync({
          folderId,
          data: requestData,
        });
        captureEvent(AA_EVENTS.FOLDER_FILE_ADDED, {
          folder_id: folderId,
          source,
          dialogue_id: dialogueId,
          artifact_id: artifactId,
          sdr_id: sdrId,
          deconflicted: result.deconflicted ?? false,
          from_origin_folder: folderName === null,
        });
        setOpen(false);
        const destLabel = folderName ?? "this project";
        if (onAdded) {
          // Caller owns feedback (generated-doc card focus-jump, #20423 WS7).
          onAdded({ folderId, folderName, fileName: result.name });
        } else if (result.deconflicted && result.original_name) {
          // Name collision: the file landed as a "(N)" copy. Offer to replace
          // the existing file in place instead (ReplaceFileDialog below).
          setConflict({
            folderId,
            folderName: destLabel,
            originalName: result.original_name,
            deconflictedName: result.name,
            request: requestData,
          });
        } else {
          setSuccess({
            text: `Added "${result.name}" to ${destLabel}`,
            folderId,
          });
        }
      } catch (err: unknown) {
        const detail = (err as any)?.response?.data?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : err instanceof Error
              ? err.message
              : "Failed to add file";
        setError(msg);
      }
    };

    // Always open the picker so the destination is an explicit choice; the chat's
    // own project is pinned at the top.
    const handleClick = () => setOpen(true);

    // null label for the anchored folder keeps "this project" + origin telemetry.
    const handlePick = (f: ProjectFolder) =>
      void addToFolder(f.id, f.id === originFolderId ? null : f.name);

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddToPhotosIcon />}
          onClick={handleClick}
          disabled={addFromWorkspace.isLoading}
          data-attr="projects:btn:add-to-project"
          sx={{ minWidth: 140 }}
        >
          Add to Project
        </Button>
        <ProjectPickerDialog
          open={open}
          onClose={() => setOpen(false)}
          title={<>Add &ldquo;{filename}&rdquo; to a project</>}
          onPick={handlePick}
          busy={addFromWorkspace.isLoading}
          pinnedFolderId={originFolderId}
          pinnedFolder={originFolder}
          emptyMessage="Create a writable project folder before adding this file."
          testIdPrefix="add-to-project"
          newProjectDataAttr="projects:btn:add-to-project-new-project"
        />
        <ReplaceFileDialog
          conflict={conflict}
          onKeepBoth={() => {
            if (conflict) {
              setSuccess({
                text: `Added "${conflict.deconflictedName}" to ${conflict.folderName}`,
                folderId: conflict.folderId,
              });
            }
            setConflict(null);
          }}
          onReplaced={(resolved, cleanupWarning) => {
            setSuccess({
              text:
                `Replaced "${resolved.originalName}" in ${resolved.folderName}` +
                (cleanupWarning ? ` — but ${cleanupWarning}` : ""),
              folderId: resolved.folderId,
            });
            setConflict(null);
          }}
          onError={(msg) => {
            setError(msg);
            setConflict(null);
          }}
        />
        <Snackbar
          open={!!error}
          autoHideDuration={5000}
          onClose={() => setError(null)}
          // Bottom-center clears the side panels; the default bottom-left tucks
          // this behind the workspace/right sidebar (matches FolderContentsList).
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="success"
            onClose={() => setSuccess(null)}
            action={
              <Button
                color="inherit"
                size="small"
                data-attr="projects:btn:view-added-project"
                onClick={() => {
                  if (success) {
                    history.push(
                      `/assistant/projects/folder/${success.folderId}`,
                    );
                  }
                  setSuccess(null);
                }}
              >
                View project
              </Button>
            }
          >
            {success?.text}
          </Alert>
        </Snackbar>
      </>
    );
  },
);

AddToProjectButton.displayName = "AddToProjectButton";

export default AddToProjectButton;
